import { ConfigError, loadConfig } from './config';
import { setRuntimeConfig } from './runtime';
import { configureAuth, whenSessionEnds } from './auth';
import { app, describeError, go, landOnStart, leaveChildMode } from './state/app';

/**
 * Everything that has to happen before React is allowed to paint, and the
 * decision about where the parent lands.
 *
 * ITS OWN MODULE, not four statements in main.tsx, because the boot sequence is
 * where this app's worst bug lived and an entry point cannot be tested: main.tsx
 * calls createRoot(document.getElementById('root')!) at import time. Pulling the
 * sequence out is what makes boot.test.tsx possible, and boot.test.tsx is the
 * only thing standing between us and a repeat of the loop described below.
 *
 * THE LOOP, for whoever reads this at 3am. A parent signed in yesterday. Entra's
 * SPA refresh tokens last 24 hours and do not renew, so the credentials are dead
 * — but the account entity survives in localStorage, boot found it, and the app
 * went to the gallery. The gallery's first call could not renew silently, so
 * auth.ts sent the whole page to Microsoft with no user gesture behind it. The
 * parent cancelled. Entra came back with #error=access_denied, which made
 * handleRedirectPromise() reject, which came out of configureAuth as a boot
 * failure — a full-screen panel whose remediation text blamed /config.json, with
 * no button and no way back to sign-in. Reloading did not help: the fragment was
 * already consumed, the account was still there, and the whole thing ran again.
 *
 * Three changes broke it, and all three are needed: auth.ts no longer redirects
 * without a gesture, a refusal from Entra drops the stale account instead of
 * keeping it, and a refusal is routed to the sign-in screen rather than to a
 * panel that cannot be dismissed.
 */
export async function boot(): Promise<void> {
  // Registered BEFORE anything can make a call, because the call that discovers
  // a dead session is fired from a mount effect one tick after the first paint.
  //
  // Child mode is dropped along with the session, deliberately. App.tsx renders
  // the sign-in screen ahead of the child shell, so leaving childMode set would
  // put a signed-out parent surface behind a PIN that no longer guards
  // anything; and the alternative — keeping the child in a shell whose every
  // call now 401s — is a toddler staring at an error. The PIN's job is to keep
  // the child out of a WORKING parent app, and there is no longer one to
  // protect: the only control on the sign-in screen is a trip to Microsoft that
  // wants a password.
  whenSessionEnds(() => {
    // leaveChildMode() rather than childMode:false in the patch below, because
    // the lock is written to sessionStorage now: clearing only the store would
    // leave a lock on disk that the next reload restores, over a signed-out app.
    leaveChildMode();
    app.set({
      signedInAs: null,
      signInNotice:
        'Microsoft signed you out — sessions here last about a day. Sign in ' +
        'again and you will come straight back.',
    });
    // go(), not a screen written into the patch above. Screens have URLs now and
    // only state/app.ts may move one, so that the address bar cannot be left
    // naming a library this parent can no longer load.
    go('signin');
  });

  try {
    const config = await loadConfig();
    setRuntimeConfig(config);

    // configureAuth is where handleRedirectPromise() runs, so this await is the
    // step that redeems the #code= fragment Entra just sent the parent back
    // with. It resolves rather than throws for everything Entra can say no to.
    const outcome = await configureAuth(config);

    if (outcome.kind === 'refused') {
      // A sign-in that did not complete: cancelled, MFA failed, conditional
      // access blocked the device, or apiScope names a scope the registration
      // does not expose. The parent needs a button, not an incident report, so
      // Microsoft's own words go under the button rather than into a panel.
      app.set({ signedInAs: null, signInNotice: outcome.message });
      landOnStart();
      return;
    }

    // A cached account means we can skip the sign-in screen entirely. Entra's
    // SPA refresh tokens last 24 hours and do not renew, so this is true for a
    // day at a time rather than Cognito's month.
    app.set(
      outcome.kind === 'signed-in'
        ? { signedInAs: outcome.email, signInNotice: null }
        : { signedInAs: null, signInNotice: null },
    );

    // WHERE THEY LAND IS THE URL'S ANSWER NOW, not a hard-coded 'gallery'.
    // Screens have paths, so a parent who bookmarked their library, or tapped a
    // link to a film, has to arrive at the thing they asked for; and the same
    // call is what sends a signed-out visitor to the sign-in screen and a cold
    // mid-wizard URL back to the gallery. It runs LAST because it reads
    // signedInAs — see landOnStart().
    //
    // Entra never sees any of these paths. MSAL's redirectUri is
    // window.location.origin and the registration lists exactly that, so a
    // sign-in started from /library still returns to `/`; a deep link cannot
    // produce a redirect_uri_mismatch.
    landOnStart();
  } catch (err) {
    // What is left is a deployment that cannot work at all: no config.json, a
    // config missing keys, the placeholder client id, or MSAL refusing to
    // initialize. `configFault` is what stops the panel from asserting a cause
    // it does not know — it used to tell every one of these to go and look at
    // /config.json, which for an Entra failure sent whoever was debugging to
    // the wrong file entirely.
    app.set({
      bootError: { message: describeError(err), configFault: err instanceof ConfigError },
    });
  }
}
